import { call, put, select, takeEvery } from 'typed-redux-saga'
import {
	addressErrorsHandler,
	commonPatentsSelector,
	commonPatentsSlice,
	formErrorsHandler,
	orderEditApi,
	OrderEditNS,
	orderEditSelectors,
	orderEditSlice,
	patentsApi,
	RbidosRequestStatuses,
	SuccessRestartModal,
} from 'Pages/registratorCabinet'
import { routerActions } from 'connected-react-router'
import { FrontUrls } from 'Constants/FrontUrls'
import {
	dadataApi,
	errorHandler,
	loadersSlice,
	modalsSlice,
	NOTIFICATION_TYPE,
	notifyMessageCreator,
	showNotificationAction,
} from 'Modules'
import { INITIATOR_REQUEST_RBIDOS_PAID } from 'Constants/Address.constants'
import { RequestParams } from 'Models'
import { leadApi, PARENT_TYPE_LEAD } from 'Pages/lead'
import { Deserialize } from 'cerialize'
import { cloneDeep, get, omit } from 'lodash'
import { getCodeTranslate } from 'LibNeo'
import { MODAL_NAME } from 'Constants/ModalName'
import { FormNames } from 'Constants/FormNames'
import { takeEveryUnique, toFormData } from 'Helpers'
import { errorsPosaddress } from 'Pages/registratorCabinet/constants'

export function* createRbidosRequestSaga(action: OrderEditNS.CreateRbidosRequestAction) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { data } = action.payload
		const res = yield* call(orderEditApi.createRbidosRequest, data)
		yield* put(
			routerActions.push(FrontUrls.cabinet_redesign.order_edit.replace(':id', String(res.id)))
		)
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

export function* createRbidosLeadSaga(action: OrderEditNS.CreateRbidosLeadAction) {
	try {
		const { data, callback } = action.payload
		const res = yield* call(orderEditApi.createRbidosLead, data)
		yield* put(orderEditSlice.actions.setEditRbidosLead(res))
		callback?.(res)
	} catch (error) {
		const { errorCallback } = action.payload
		if (errorCallback) {
			errorCallback?.(error?.data?.errors)
		} else {
			yield* call(errorHandler, error)
		}
	}
}

export function* getEditRbidosRequestSaga(action: OrderEditNS.GetEditRbidosRequest) {
	try {
		const { id } = action.payload
		yield* put(loadersSlice.actions.showGlobalLoader())
		const res = yield* call(orderEditApi.getEditRbidosRequest, id)
		yield* put(orderEditSlice.actions.setOne(res))
		yield* put(loadersSlice.actions.hideGlobalLoader())
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		const errors = error?.data?.errors
		yield* call(notifyMessageCreator, 'rbidos.get_order.error', {
			data: errors,
			options: { type: NOTIFICATION_TYPE.ERROR },
		})
	}
}

export function* updateEditRbidosRequestSaga(action: OrderEditNS.UpdateEditRbidosRequest) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { id, data, callback } = action.payload
		const patents = yield* select(commonPatentsSelector.getPatents)
		if (!data.patent_application_enabled && patents.length) {
			yield* call(patentsApi.disablePatent, id)
			yield* put(commonPatentsSlice.actions.clearPatents())
		}
		const res = yield* call(orderEditApi.updateEditRbidosRequest, id, omit(data, 'user_docs'))
		yield* put(orderEditSlice.actions.setOne(res))
		yield* call(notifyMessageCreator, 'common.save.success')
		yield* put(loadersSlice.actions.hideGlobalLoader())
		callback?.(res)
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		const { errorCallback } = action.payload
		const { data } = action.payload
		if (
			error.data?.errors
				?.map((i) => Object.keys(i))
				.flat()
				.some((item): boolean => errorsPosaddress.includes(item))
		) {
			yield* call(notifyMessageCreator, 'orders.address_terminal_error.error', {
				options: { type: NOTIFICATION_TYPE.ERROR },
			})
		}
		errorCallback?.(error.data?.errors)
		const errors = addressErrorsHandler(error, data)
		if (!error.data.errors[0]?.external_errors) {
			const notificationErrors = formErrorsHandler(cloneDeep(errors))
			yield* call(errorHandler, errors, FormNames.REGISTRATOR_CABINET.ORDER_EDIT)
			yield* call(errorHandler, notificationErrors)
		}
	}
}

export function* pushEditRbidosRequestSaga(action: OrderEditNS.PushEditRbidosRequest) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { id, data, callback, isQrCodeForSbol } = action.payload
		const patents = yield* select(commonPatentsSelector.getPatents)
		if (!data.patent_application_enabled && patents.length) {
			yield* call(patentsApi.disablePatent, id)
			yield* put(commonPatentsSlice.actions.clearPatents())
		}
		yield* call(orderEditApi.updateEditRbidosRequest, id, omit(data, 'user_docs'))
		const res = yield* call(orderEditApi.pushEditRbidosRequest, id, omit(data, 'user_docs'))
		yield* put(orderEditSlice.actions.setOne(res))
		yield* put(loadersSlice.actions.hideGlobalLoader())
		callback?.(res, isQrCodeForSbol)
	} catch (error) {
		const { id, errorCallback } = action.payload
		const res = yield* call(orderEditApi.getEditRbidosRequest, id)
		yield* put(orderEditSlice.actions.setOne(res))
		yield* put(loadersSlice.actions.hideGlobalLoader())
		const { data } = action.payload
		if (
			error.data?.errors
				?.map((i) => Object.keys(i))
				.flat()
				.some((item): boolean => errorsPosaddress.includes(item))
		) {
			yield* call(notifyMessageCreator, 'orders.address_terminal_error.error', {
				options: { type: NOTIFICATION_TYPE.ERROR },
			})
		}
		errorCallback?.(error.data?.errors)
		const errors = addressErrorsHandler(error, data)
		if (!error.data.errors[0]?.external_errors) {
			const notificationErrors = formErrorsHandler(cloneDeep(errors))
			yield* call(errorHandler, errors, FormNames.REGISTRATOR_CABINET.ORDER_EDIT)
			yield* call(errorHandler, notificationErrors)
		}
	}
}

export function* rollbackStatusRbidosRequestSaga(action: OrderEditNS.rollbackStatusRbidosRequest) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { id, callback } = action.payload
		const res = yield* call(orderEditApi.rollbackStatusRbidosRequest, id)
		yield* put(orderEditSlice.actions.setOne(res))
		yield* call(notifyMessageCreator, 'common.rollback_request_status.success')
		yield* put(loadersSlice.actions.hideGlobalLoader())
		callback?.(res)
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		const { errorCallback } = action.payload
		const errors = error?.data?.errors
		yield* put(
			showNotificationAction({
				type: NOTIFICATION_TYPE.ERROR,
				message: 'Ошибка при изменении статуса на предыдущий',
			})
		)
		errorCallback?.(errors)
	}
}

export function* getInnRbidosRequestSaga(action: OrderEditNS.GetInnRbidosRequest) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { callback, data } = action.payload
		const res = yield* call(orderEditApi.getInnRbidosRequest, data)
		yield* call(notifyMessageCreator, 'common.get_inn.success')
		yield* put(loadersSlice.actions.hideGlobalLoader())
		callback?.(Deserialize(res).inn)
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		const { errorCallback } = action.payload
		const errors = error.data.errors ?? []
		errorCallback?.(errors)
	}
}

export function* sendCodeEmailRbidosRequestSaga(action: OrderEditNS.SendCodeEmailRbidosRequest) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { id, data, flagAgain, callback } = action.payload
		let res
		res = yield* call(orderEditApi.updateEditRbidosRequest, id, { ...data })
		if (flagAgain) {
			res = yield* call(orderEditApi.rollbackStatusRbidosRequest, id)
			res = yield* call(orderEditApi.pushEditRbidosRequest, id, { ...res })
		} else {
			const { code } = data
			res = yield* call(orderEditApi.pushEditRbidosRequest, id, { ...res, code })
		}
		yield* put(orderEditSlice.actions.setOne(res))
		yield* put(loadersSlice.actions.hideGlobalLoader())
		callback?.(res)
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		const { errorCallback } = action.payload
		const errors = error.data.errors ?? []
		yield* call(errorHandler, error, FormNames.REGISTRATOR_CABINET.EMAIL_OR_PHONE_CONFIRMATION)
		errorCallback?.(errors)
	}
}

export function* sendRemoteDocumentsSaga(action: OrderEditNS.sendRemoteDocuments) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		yield* call(orderEditApi.sendRemoteDocuments, action.payload.data)
		yield* call(notifyMessageCreator, 'rbidos.send_remote_documents')
		yield* put(modalsSlice.actions.hideModal(MODAL_NAME.CLIENT_DOCUMENTS))
	} catch (error) {
		const codeError = get(Object.values(get(error, 'data.errors[0]', {})), '[0]', '')
		action?.payload.callback(codeError)
		yield* put(
			showNotificationAction({
				type: NOTIFICATION_TYPE.ERROR,
				message: getCodeTranslate(`errors.rbidos.${codeError}`),
			})
		)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}
function* saveRequest(action: OrderEditNS.SaveRequest) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const res = yield* call(orderEditApi.copyRbidosRequest, action.payload.id, action.payload.data)
		const data = yield* call(orderEditApi.getEditRbidosRequest, res.id)
		yield* put(orderEditSlice.actions.setOne(data))
		window.location.href = FrontUrls.cabinet_redesign.order_edit.replace(':id', String(data.id))
		yield* call(notifyMessageCreator, 'common.save.success')
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* getSocketRbidosRequest(action: OrderEditNS.getSocket) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const res = yield* call(orderEditApi.getEditRbidosRequest, action.payload.id)
		yield* put(orderEditSlice.actions.setSocketRequest(res))
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* updateSocketId(action: OrderEditNS.updateSocketId) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const order = yield* select(orderEditSelectors.getOne)
		if (order?.id && order?.id === action.payload.id) {
			yield* call(
				getEditRbidosRequestSaga,
				orderEditSlice.actions.getOne({ id: action.payload.id })
			)
			const res = yield* select(orderEditSelectors.getOne)
			if (res.status !== RbidosRequestStatuses.S_CBOL_WAITING) {
				yield* put(modalsSlice.actions.hideModal(MODAL_NAME.S_CBOL_WAITING))
			}
			if (
				JSON.stringify(order?.user_docs.user_pasport_mains) !==
					JSON.stringify(res.user_docs?.user_pasport_mains) ||
				JSON.stringify(order?.user_docs.user_passport_fulls) !==
					JSON.stringify(res.user_docs?.user_passport_fulls)
			) {
				yield* put(orderEditSlice.actions.setDocumentsUpdate(true))
			}
		}
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* getSberIdInfo(action: OrderEditNS.getSberIdInfo) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const allLeads = yield* call(
			leadApi.getAll,
			new RequestParams(1, 10, null, {
				parent_id: action.payload,
				parent_type: PARENT_TYPE_LEAD.REQUEST,
			})
		)
		if (allLeads?.total_entries) {
			const sberIdInfo = yield* call(orderEditApi.getSberIdInfo, allLeads?.entries?.[0]?.id)
			let dadataInfo
			if (Object.keys(sberIdInfo?.address_reg ?? {}).length) {
				const query = sberIdInfo?.address_reg?.full_address
				dadataInfo = yield* call(
					dadataApi.getAddress,
					{ query, origin: INITIATOR_REQUEST_RBIDOS_PAID },
					'address'
				)
			}
			yield* put(
				orderEditSlice.actions.setSberIdInfo({
					...sberIdInfo,
					...(dadataInfo?.[0] && { dadataInfo: dadataInfo?.[0] }),
				})
			)
		}
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* restartRbidosRequest(action: OrderEditNS.restartRbidosRequest) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const dataForApi = toFormData(action.payload?.data, { prepareFiles: 'files_attributes' })

		if (action.payload?.data?.body === undefined) dataForApi.set('[data][body]', '')
		yield* call(orderEditApi.restartApi, action.payload?.data?.id, dataForApi)
		yield* put(
			modalsSlice.actions.showModal({
				Component: SuccessRestartModal,
				key: MODAL_NAME.RESTART_ORDER_SUCCESS,
				componentProps: { email: action.payload.data?.email },
			})
		)
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* deleteRbidosRequest(action: OrderEditNS.DeleteRbidosRequest) {
	try {
		const { id, callback } = action.payload
		yield* put(loadersSlice.actions.showGlobalLoader())
		const res = yield* call(orderEditApi.deleteRbidosRequest, id)
		callback?.(res)
		yield* call(notifyMessageCreator, 'rbidos.delete_request.success')
	} catch (error) {
		yield* put(
			showNotificationAction({
				type: NOTIFICATION_TYPE.ERROR,
				message: 'Ошибка при удалении заявки в кабинете регистратора',
			})
		)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* restoreRbidosRequest(action: OrderEditNS.RestoreRbidosRequest) {
	try {
		const { id, callback } = action.payload
		yield* put(loadersSlice.actions.showGlobalLoader())
		const res = yield* call(orderEditApi.restoreRbidosRequest, id)
		callback?.(res)
		yield* call(notifyMessageCreator, 'rbidos.restore_request.success')
	} catch (error) {
		yield* put(
			showNotificationAction({
				type: NOTIFICATION_TYPE.ERROR,
				message: 'Ошибка при восстановлении заявки в кабинете регистратора',
			})
		)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* sendStatusPaymentCallback(action: OrderEditNS.SendStatusPaymentCallback) {
	try {
		const res = yield* call(orderEditApi.sendStatusPaymentCallback, action.payload?.data)
		// eslint-disable-next-line no-console
		console.log(res)
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* getMccCodes(action: OrderEditNS.GetMccCodes) {
	try {
		const params: RequestParams = action.payload.requestParams
		const res = yield* call(orderEditApi.getMccCodes, params)
		yield* put(orderEditSlice.actions.setMccCodes(res))
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* getMccGroups(action: OrderEditNS.GetMccGroups) {
	try {
		const params: RequestParams = action.payload.requestParams
		const res = yield* call(orderEditApi.getMccGroups, params)
		yield* put(orderEditSlice.actions.setMccGroups(res))
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

export function* getRbidosLeadsSaga(action: OrderEditNS.GetRbidosLeads) {
	try {
		const { params, callback } = action.payload
		const res = yield* call(orderEditApi.getRbidosLeads, params)
		const lead = res?.entries?.[0]
		yield* put(orderEditSlice.actions.setEditRbidosLead(lead))
		callback?.(lead)
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

export function* orderEditSaga() {
	yield* takeEvery(orderEditSlice.actions.createOne.type, createRbidosRequestSaga)
	yield* takeEveryUnique(orderEditSlice.actions.createRbidosLead.type, createRbidosLeadSaga)
	yield* takeEvery(orderEditSlice.actions.getOne.type, getEditRbidosRequestSaga)
	yield* takeEvery(orderEditSlice.actions.updateOne.type, updateEditRbidosRequestSaga)
	yield* takeEvery(orderEditSlice.actions.pushRbidosRequest.type, pushEditRbidosRequestSaga)
	yield* takeEvery(
		orderEditSlice.actions.rollbackStatusRbidosRequest.type,
		rollbackStatusRbidosRequestSaga
	)
	yield* takeEvery(orderEditSlice.actions.getInnRbidosRequest.type, getInnRbidosRequestSaga)
	yield* takeEvery(
		orderEditSlice.actions.sendCodeEmailRbidosRequest.type,
		sendCodeEmailRbidosRequestSaga
	)
	yield* takeEvery(orderEditSlice.actions.sendRemoteDocuments.type, sendRemoteDocumentsSaga)
	yield* takeEveryUnique(orderEditSlice.actions.saveRequest.type, saveRequest)
	yield* takeEvery(orderEditSlice.actions.getSocket.type, getSocketRbidosRequest)
	yield* takeEvery(orderEditSlice.actions.updateSocketId.type, updateSocketId)
	yield* takeEvery(orderEditSlice.actions.getSberIdInfo.type, getSberIdInfo)
	yield* takeEvery(orderEditSlice.actions.restartRbidosRequest.type, restartRbidosRequest)
	yield* takeEvery(orderEditSlice.actions.deleteRbidosRequest.type, deleteRbidosRequest)
	yield* takeEvery(orderEditSlice.actions.restoreRbidosRequest.type, restoreRbidosRequest)
	yield* takeEvery(orderEditSlice.actions.sendStatusPaymentCallback.type, sendStatusPaymentCallback)
	yield* takeEvery(orderEditSlice.actions.getMccCodes.type, getMccCodes)
	yield* takeEvery(orderEditSlice.actions.getMccGroups.type, getMccGroups)
	yield* takeEveryUnique(orderEditSlice.actions.getRbidosLeads.type, getRbidosLeadsSaga)
}
